import SEO from "../../components/SEO";
import banner from "../../assets/img/delivery-illustration.webp";
import trustedClients from "../../constants/trusted-clients";
import {
	BsArrowRight,
	BsBuildings,
	BsCart3,
	BsCodeSlash,
	BsGeoAlt,
	BsPerson,
	BsRecycle,
	BsSignpostSplit,
	BsTree,
} from "react-icons/bs";

function SectionHeading({ eyebrow, title, children }) {
	return (
		<div className="max-w-3xl mb-10">
			<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">{eyebrow}</p>
			<h2 className="text-3xl md:text-4xl text-gray-900 mb-4">{title}</h2>
			{children && <p className="text-lg text-gray-600">{children}</p>}
		</div>
	);
}

function FeatureCard({ icon, title, children }) {
	return (
		<div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
			<span className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50 text-purple-600 text-2xl mb-5" aria-hidden="true">
				{icon}
			</span>
			<h3 className="text-xl text-gray-900 mb-2">{title}</h3>
			<p className="text-gray-600 leading-relaxed">{children}</p>
		</div>
	);
}

function AudienceCard({ icon, title, points, link }) {
	return (
		<div className="flex flex-col bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
			<div className="flex items-center gap-3 mb-4">
				<span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600 text-white text-lg" aria-hidden="true">
					{icon}
				</span>
				<h3 className="text-xl text-gray-900">{title}</h3>
			</div>
			<ul className="space-y-2 text-gray-600 flex-1">
				{points.map((point) => (
					<li key={point} className="flex gap-2">
						<span className="mt-2 h-1.5 w-1.5 rounded-full bg-purple-400 flex-shrink-0" aria-hidden="true" />
						{point}
					</li>
				))}
			</ul>
			<a href={link.href} className="mt-5 inline-flex items-center gap-2 self-start text-sm font-medium text-purple-700 hover:text-purple-900">
				{link.label} <BsArrowRight aria-hidden="true" />
			</a>
		</div>
	);
}

export const AboutPage = () => {
	const limpopoTowns = ["Polokwane", "Giyani", "Malamulele", "Tzaneen", "Bungeni", "Thohoyandou", "Phalaborwa"];

	return (
		<div className="w-full flex flex-col bg-gradient-to-br from-cyan-100 to-fuchsia-200">
			<SEO
				title="About Us"
				description="Parcelin is a Liftsearch innovation delivering parcels affordably across Gauteng and Limpopo, using vehicles already on the road."
				path="/about-us"
			/>

			{/* Hero */}
			<section className="max-w-6xl mx-auto w-full px-4 sm:px-5 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
				<div>
					<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-4">About Parcelin</p>
					<h1 className="text-4xl md:text-5xl text-purple-800 leading-tight mb-6">
						Revolutionising long-distance parcel delivery, one parcel at a time.
					</h1>
					<p className="text-lg text-gray-700 mb-8 max-w-xl">
						Parcelin was born out of the demand for reliable, cheaper same-day parcel delivery in South Africa.
						We connect your parcels with vehicles already travelling your route, so delivery costs less and puts
						fewer cars on the road.
					</p>
					<div className="flex flex-wrap gap-3">
						<a href="/" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-full transition-colors">
							Send a parcel <BsArrowRight aria-hidden="true" />
						</a>
						<a href="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-purple-50 text-purple-700 font-semibold px-6 py-3 rounded-full border border-purple-200 transition-colors">
							Contact us
						</a>
					</div>
				</div>
				<div className="flex justify-center">
					<img className="w-full max-w-md h-auto object-contain drop-shadow-xl" src={banner} width="904" height="942" alt="Parcelin delivery illustration" />
				</div>
			</section>

			{/* Story */}
			<section className="bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-5 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-5 gap-10">
					<div className="lg:col-span-2">
						<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Our story</p>
						<h2 className="text-3xl md:text-4xl text-gray-900">Yet another Liftsearch innovation</h2>
					</div>
					<div className="lg:col-span-3 space-y-5 text-lg text-gray-600 leading-relaxed">
						<p>
							Parcelin grew out of{" "}
							<a href="https://liftsearch.co.za" target="_blank" rel="noopener noreferrer" className="text-purple-700 font-semibold underline decoration-purple-300 underline-offset-4 hover:decoration-purple-700">
								Liftsearch
							</a>
							, the ride-sharing platform. If drivers are making the trip anyway, the space in their vehicles can carry
							parcels too. It's carpooling, but for packages.
						</p>
						<p>
							We started with the routes we know best, between Gauteng and towns across Limpopo, and built Parcelin so that
							individuals and businesses can book, pay for and track a delivery in minutes.
						</p>
					</div>
				</div>
			</section>

			{/* Sustainability */}
			<section className="max-w-6xl mx-auto w-full px-4 sm:px-5 py-14 md:py-20">
				<SectionHeading eyebrow="Delivering sustainably" title="Fewer vehicles, same delivery">
					Traditional couriers add dedicated vehicles to already crowded roads. We use the trips that are already happening.
				</SectionHeading>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
					<FeatureCard icon={<BsTree />} title="Reduced emissions">
						Using vehicles that are making the journey anyway means less fuel burned and a smaller carbon footprint for every parcel.
					</FeatureCard>
					<FeatureCard icon={<BsSignpostSplit />} title="Smart route matching">
						We match parcels with drivers already travelling the route, filling spare space instead of sending another vehicle.
					</FeatureCard>
					<FeatureCard icon={<BsRecycle />} title="A greener network">
						Every parcel delivered through Parcelin helps build a delivery network that's better for South Africa and the planet.
					</FeatureCard>
				</div>
			</section>

			{/* Who we serve */}
			<section className="bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-5 py-14 md:py-20">
					<SectionHeading eyebrow="Who we deliver for" title="Built for people and businesses" />
					<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
						<AudienceCard
							icon={<BsPerson />}
							title="Individuals"
							points={["Instant quotes, no sign-up needed", "Same-day and next-day options", "Track your parcel with its tracking number"]}
							link={{ href: "/", label: "Get a quote" }}
						/>
						<AudienceCard
							icon={<BsBuildings />}
							title="Businesses"
							points={["A business dashboard for all your orders", "Bulk uploads for many parcels at once", "Pay per order, monthly invoicing or prepaid"]}
							link={{ href: "/signup", label: "Open a business account" }}
						/>
						<AudienceCard
							icon={<BsCart3 />}
							title="Online stores"
							points={["Shopify and WooCommerce integrations", "An API to create and track orders", "Waybills ready to print"]}
							link={{ href: "/developers", label: "View the API docs" }}
						/>
					</div>
				</div>
			</section>

			{/* Where we operate */}
			<section className="max-w-6xl mx-auto w-full px-4 sm:px-5 py-14 md:py-20">
				<SectionHeading eyebrow="Where we operate" title="Gauteng and Limpopo, from two bases" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
					<div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
						<h3 className="text-xl text-gray-900 mb-4">Delivery areas</h3>
						<p className="font-semibold text-gray-900 mb-2">Limpopo</p>
						<ul className="flex flex-wrap gap-2 mb-5">
							{limpopoTowns.map((town) => (
								<li key={town} className="text-sm font-medium text-purple-800 bg-purple-50 border border-purple-100 rounded-full px-3 py-1">
									{town}
								</li>
							))}
						</ul>
						<p className="font-semibold text-gray-900 mb-1">Gauteng</p>
						<p className="text-gray-600">Within 70km of our Midrand warehouse</p>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
						{[
							{ tag: "Warehouse", name: "Midrand", detail: "Gallagher Business Exchange, Halfway House" },
							{ tag: "Office", name: "Giyani", detail: "Bafana Hardware Building, Giyani CBD" },
						].map((office) => (
							<div key={office.name} className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
								<span className="text-xs font-medium uppercase tracking-wide text-purple-700 bg-purple-50 rounded-full px-3 py-1">
									{office.tag}
								</span>
								<div className="flex items-start gap-2 mt-4">
									<BsGeoAlt className="text-purple-600 mt-1 flex-shrink-0" aria-hidden="true" />
									<div>
										<h3 className="text-lg text-gray-900">{office.name}</h3>
										<p className="text-sm text-gray-600">{office.detail}</p>
									</div>
								</div>
							</div>
						))}
						<a href="/contact" className="sm:col-span-2 inline-flex items-center justify-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900 bg-white/70 rounded-2xl border border-purple-100 py-3">
							Full addresses and directions <BsArrowRight aria-hidden="true" />
						</a>
					</div>
				</div>
			</section>

			{/* Trusted by */}
			{trustedClients.length > 0 && (
				<section className="bg-white">
					<div className="max-w-6xl mx-auto px-4 sm:px-5 py-14">
						<p className="text-center text-sm font-medium uppercase tracking-widest text-purple-600 mb-8">
							Brands that trust Parcelin
						</p>
						<ul className="flex flex-wrap justify-center gap-4">
							{trustedClients.map((client) => (
								<li key={client.name}>
									<a
										href={client.link}
										target="_blank"
										rel="noopener noreferrer"
										className="flex h-20 w-40 items-center justify-center rounded-xl border border-gray-100 bg-white p-3 shadow-sm hover:shadow-md transition-shadow"
									>
										<img src={client.logo} alt={client.name} className="max-h-14 w-full object-contain" />
									</a>
								</li>
							))}
						</ul>
					</div>
				</section>
			)}

			{/* CTA */}
			<section className="max-w-6xl mx-auto w-full px-4 sm:px-5 py-14 md:py-20">
				<div className="rounded-3xl bg-gradient-to-br from-purple-700 to-fuchsia-600 text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6">
					<div className="flex-1">
						<h2 className="text-3xl mb-3">A greener way to send your parcels</h2>
						<p className="text-white/85 max-w-xl">
							When you choose Parcelin, you're choosing convenience and affordability, and fewer vehicles on the road.
						</p>
					</div>
					<div className="flex flex-wrap gap-3">
						<a href="/" className="inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 font-semibold px-6 py-3 rounded-full transition-colors">
							Get a quote <BsArrowRight aria-hidden="true" />
						</a>
						<a href="/developers" className="inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 font-semibold px-6 py-3 rounded-full transition-colors">
							<BsCodeSlash aria-hidden="true" /> For developers
						</a>
					</div>
				</div>
			</section>
		</div>
	);
};
